/**
 * Client port of the server's temporal aggregation (`sf_to_gf` /
 * `do_temporal_agg` in `engine/forecast_grid.py`): roll the recomputed weekly
 * frame up into the month (M01..M12), quarter (Q1..Q4), half-year (H1..H2),
 * PTG and TTL columns the grid shows.
 *
 * Server algorithm mirrored here:
 *  1. For each (metric, fiscal year, period) aggregate the period's weeks with
 *     the metric's rule from `metricTemporalAgg` (pandas `groupby.agg(rule)`):
 *       - sum   → Σ of finite weeks; all-missing → NaN (pandas min_count=1)
 *       - first → first finite week; none → NaN
 *       - last  → last finite week;  none → NaN
 *       - mean  → mean of finite weeks; none → NaN
 *  2. Recompute the derived sales metrics from the aggregated irreducibles
 *     (`calculate_derived_sales_kpis`), so a derived metric's step-1 value
 *     (e.g. a `mean` placeholder for a ratio) is overwritten by its formula —
 *     a ratio of the period sums, not the mean of weekly ratios.
 *
 * The derivation set is `TEMPORAL_AGG_DERIVATIONS`: `SALES_KPI_DERIVATIONS` —
 * the same validated server mirror the summary grid uses — plus POTB's AUC and
 * `WEEK_COVER_DERIVATIONS`, which only this consumer can produce (its
 * denominator is a `mean` aggregate). NOT the
 * generated `DERIVED_KPI_EXPRESSIONS` registry (which only covers weekly-only
 * OPTION and per-SQM metrics that `sf_to_gf` does not recompute). Parity with
 * the server is validated by the temporal-aggregate vectors.
 */

import {
  type DerivedMetricDef,
  type MetricValues,
  recalcDerivedMetrics
} from "../summary/editPropagation";
import {
  NON_SUMMABLE_MARKDOWN_METRICS,
  POTB_AUC_DERIVATION,
  RATE_OF_SALES_SOURCES,
  SALES_KPI_DERIVATIONS,
  WEEK_COVER_DERIVATIONS
} from "../summary/salesKpiDerivations";

export type TemporalAggRule = "sum" | "first" | "last" | "mean";

/** The rule a period of `metric` aggregates by; a metric no rule names sums.
 *  The period-edit spread reads it too, so a period shows the typed value. */
export function temporalAggRule(
  metric: string,
  metricTemporalAgg: Record<string, string>
): TemporalAggRule {
  return (metricTemporalAgg[metric] ?? "sum") as TemporalAggRule;
}

/**
 * TS AUC is a price shown as its period's last week; a period entry still
 * prices every week of it (`spreads_every_week`).
 */
export function spreadsEveryWeek(metric: string): boolean {
  return metric === "TARGET_STOCK_AUC";
}

/**
 * The summary derivations plus POTB's AUC. POTB units / at-cost aggregate by
 * `sum` from the metric rules, so their ratio has to be recomputed from the
 * period sums rather than averaged across weeks.
 */
const TEMPORAL_AGG_DERIVATIONS: DerivedMetricDef[] = [
  ...SALES_KPI_DERIVATIONS,
  POTB_AUC_DERIVATION,
  ...WEEK_COVER_DERIVATIONS
];

/** The derivations a period recomputes: all but the stock-balance-derived ones. */
function periodDerivationsOf(derived: DerivedMetricDef[]): DerivedMetricDef[] {
  return derived.filter((d) => !d.stockBalanceDerived);
}

/**
 * Whether a period's value of `metric` is recomputed from the period's
 * aggregated inputs (step 2) rather than taken from its rule's aggregate — a
 * ratio of the period sums, for one. Markdown ratios a period blanks are not.
 */
export function rederivedOverPeriod(metric: string): boolean {
  return (
    !NON_SUMMABLE_MARKDOWN_METRICS.includes(metric) &&
    periodDerivationsOf(TEMPORAL_AGG_DERIVATIONS).some(
      (def) => def.name === metric
    )
  );
}

export const TOTAL_LABEL = "TTL";
export const PTG_LABEL = "PTG";

/** The period labels a week rolls into (within its fiscal year). */
export interface WeekPeriods {
  /** Month label, e.g. "M01". */
  month: string;
  /** Quarter label, e.g. "Q1". */
  quarter: string;
  /** Half-year label, e.g. "H1". */
  halfYear: string;
  /** Whether this week falls in the forward-looking "period to go". */
  inPtg: boolean;
  /** The week's `year_week`, for comparison against the forecast boundary. */
  yearWeek: number;
}

/** One week of the recomputed frame, tagged with its year and period membership. */
export interface AggWeek {
  fiscalYear: string;
  /** metric_name → value (NaN for a missing/blank week, matching the grid). */
  values: MetricValues;
  periods: WeekPeriods;
}

/** fiscalYear → periodLabel (M01..Q4, H1..H2, PTG, TTL) → (metric → aggregated value). */
export type TemporalAggregates = Record<string, Record<string, MetricValues>>;

/** A period's value by its rule over the finite weeks, as pandas aggregates. */
export function aggregate(values: number[], rule: TemporalAggRule): number {
  const finite = values.filter((v) => Number.isFinite(v));
  switch (rule) {
    case "sum":
      return finite.length ? finite.reduce((acc, v) => acc + v, 0) : Number.NaN;
    case "first":
      return finite.length ? finite[0] : Number.NaN;
    case "last":
      return finite.length ? finite[finite.length - 1] : Number.NaN;
    case "mean":
      return finite.length
        ? finite.reduce((acc, v) => acc + v, 0) / finite.length
        : Number.NaN;
  }
}

/**
 * Rate of sales and week cover on the weekly columns, where the aggregation
 * group is a single week: the rate is that week's sales and cover the plain
 * stock/sales ratio. The server's weekly columns come out of the same
 * `sf_to_gf` pass as its period columns, so they are filled here rather than in
 * the cascade engine. Mutates `weeks` in place; cover is skipped without stock.
 */
export function fillWeeklyWeekCover(weeks: MetricValues[]): void {
  for (const week of weeks) {
    for (const [rateOfSales, source] of Object.entries(RATE_OF_SALES_SOURCES)) {
      if (source in week) week[rateOfSales] = week[source];
    }
    if (!("END_OF_PERIOD_UNITS" in week)) continue;
    for (const def of WEEK_COVER_DERIVATIONS) {
      week[def.name] = def.calc(week);
    }
  }
}

/** Period buckets a week contributes to (TTL always; PTG only when in-window). */
function periodLabelsOf(periods: WeekPeriods): string[] {
  const labels = [
    periods.month,
    periods.quarter,
    periods.halfYear,
    TOTAL_LABEL
  ];
  if (periods.inPtg) labels.push(PTG_LABEL);
  return labels;
}

/** Group weeks by fiscal year, preserving frame order. */
function groupByYear(weeks: AggWeek[]): Map<string, AggWeek[]> {
  const byYear = new Map<string, AggWeek[]>();
  for (const week of weeks) {
    const bucket = byYear.get(week.fiscalYear);
    if (bucket) bucket.push(week);
    else byYear.set(week.fiscalYear, [week]);
  }
  return byYear;
}

/** Aggregate one period's weeks per the metric rules (step 1, pre-derivation). */
function aggregatePeriod(
  periodWeeks: AggWeek[],
  metricTemporalAgg: Record<string, string>
): MetricValues {
  const metrics = new Set<string>();
  for (const week of periodWeeks) {
    for (const metric of Object.keys(week.values)) metrics.add(metric);
  }
  // Rate of sales aggregates (mean) rather than derives, so it must be a
  // metric of its own here — the server attaches it the same way, before
  // aggregating (`attach_rate_of_sales`).
  for (const [rateOfSales, source] of Object.entries(RATE_OF_SALES_SOURCES)) {
    if (metrics.has(source)) metrics.add(rateOfSales);
  }

  const aggregated: MetricValues = {};
  for (const metric of metrics) {
    const rule = temporalAggRule(metric, metricTemporalAgg);
    // A rate of sales reads its source's weeks, not its own row: that is what
    // the server aggregates, whether or not the frame carries the copy.
    const read = RATE_OF_SALES_SOURCES[metric] ?? metric;
    const series = periodWeeks.map((week) =>
      read in week.values ? week.values[read] : Number.NaN
    );
    aggregated[metric] = aggregate(series, rule);
  }
  return aggregated;
}

/**
 * Aggregate a weekly frame into its temporal periods. Pure — no grid/DOM access.
 * `metricTemporalAgg` is `metaData.metricTemporalAgg`; unknown metrics fall back
 * to "sum" (the server assigns every metric a rule, so this is just a guard).
 */
export function computeTemporalAggregates(
  weeks: AggWeek[],
  metricTemporalAgg: Record<string, string>,
  derived: DerivedMetricDef[] = TEMPORAL_AGG_DERIVATIONS
): TemporalAggregates {
  const result: TemporalAggregates = {};

  for (const [fiscalYear, yearWeeks] of groupByYear(weeks)) {
    const periods: Record<string, AggWeek[]> = {};
    for (const week of yearWeeks) {
      for (const label of periodLabelsOf(week.periods)) {
        (periods[label] ??= []).push(week);
      }
    }

    // Mirror the server's calculate_derived_sales_kpis pass, which recomputes
    // the same derived metrics for every period. Stock-balance-derived metrics
    // (EOP/OTB) are excluded — they take the server's last/first aggregation
    // here rather than the balance (sf_to_gf does not run them on this path).
    const periodDerivations = periodDerivationsOf(derived);

    const yearResult: Record<string, MetricValues> = {};
    for (const [label, periodWeeks] of Object.entries(periods)) {
      const aggregated = aggregatePeriod(periodWeeks, metricTemporalAgg);
      // Overwrite the derived sales metrics from the aggregated irreducibles
      // (a ratio of period sums, not the mean of weekly ratios).
      const result = recalcDerivedMetrics(aggregated, periodDerivations);
      // FURTHER_MD_* leaves/ratios are not safely summable across weeks (an SO
      // can be marked down repeatedly), so blank them on aggregate columns. Weekly
      // columns are unaffected (they come straight from the frame, not this
      // aggregation).
      for (const metric of NON_SUMMABLE_MARKDOWN_METRICS) {
        result[metric] = Number.NaN;
      }
      yearResult[label] = result;
    }

    result[fiscalYear] = yearResult;
  }

  return result;
}
